import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import './Account.css';
import { getUser, updateProfile, getAddresses, addAddress, deleteAddress, getUserPaymentMethods, login, register, googleLogin, forgotPassword } from '../api/api';

function Account() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authMode, setAuthMode] = useState('signin'); // 'signin' or 'signup'
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({ name: '', phone: '' });
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [authForm, setAuthForm] = useState({
    email: '',
    password: '',
    name: '',
    confirmPassword: ''
  });
  const [authError, setAuthError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotStatus, setForgotStatus] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [newAddress, setNewAddress] = useState({
    label: 'Home',
    street: '',
    city: '',
    province: '',
    postalCode: '',
    country: 'South Africa',
    isDefault: false
  });

  // Load user data on mount
  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const userData = await getUser();
      setUser(userData);
      setIsAuthenticated(true);
      setProfileData({ name: userData.name, phone: userData.phone || '' });
      
      const addressesData = await getAddresses();
      setAddresses(addressesData);

      try {
        const methodsData = await getUserPaymentMethods();
        setPaymentMethods(methodsData);
      } catch (err) {
        console.log('Payment methods not available');
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      // User is not authenticated, show login/signup form
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      await updateProfile(profileData);
      setUser({ ...user, ...profileData });
      setEditingProfile(false);
      alert('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile');
    }
  };

  const handleAddAddress = async () => {
    try {
      const newAddr = await addAddress(newAddress);
      setAddresses([...addresses, newAddr]);
      setNewAddress({
        label: 'Home',
        street: '',
        city: '',
        province: '',
        postalCode: '',
        country: 'South Africa',
        isDefault: false
      });
      setShowAddressForm(false);
      alert('Address added successfully');
    } catch (error) {
      console.error('Error adding address:', error);
      alert('Failed to add address');
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;
    
    try {
      await deleteAddress(addressId);
      setAddresses(addresses.filter(addr => addr._id !== addressId));
      alert('Address deleted successfully');
    } catch (error) {
      console.error('Error deleting address:', error);
      alert('Failed to delete address');
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      const response = await login(authForm.email, authForm.password);
      localStorage.setItem('token', response.token);
      alert('Sign in successful!');
      setAuthForm({ email: '', password: '', name: '', confirmPassword: '' });
      // Reload user data
      fetchUserData();
    } catch (error) {
      console.error('Sign in error:', error);
      const errorMsg = error.response?.data?.message || 'Failed to sign in. Please check your credentials.';
      setAuthError(errorMsg);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setAuthError('');
    if (authForm.password !== authForm.confirmPassword) {
      setAuthError('Passwords do not match');
      return;
    }
    if (authForm.password.length < 6) {
      setAuthError('Password must be at least 6 characters long');
      return;
    }
    try {
      const response = await register({
        name: authForm.name,
        email: authForm.email,
        password: authForm.password
      });
      localStorage.setItem('token', response.token);
      alert('Account created successfully!');
      setAuthForm({ email: '', password: '', name: '', confirmPassword: '' });
      setAuthError('');
      // Reload user data
      fetchUserData();
    } catch (error) {
      console.error('Sign up error:', error);
      const errorMsg = error.response?.data?.message || 'Failed to create account';
      
      // If user already exists, suggest signing in
      if (errorMsg.toLowerCase().includes('already exists') || errorMsg.toLowerCase().includes('duplicate')) {
        setAuthError(`${errorMsg}. Try signing in instead.`);
      } else {
        setAuthError(errorMsg);
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setIsAuthenticated(false);
    setAddresses([]);
    setPaymentMethods([]);
    alert('Logged out successfully');
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setAuthError('');
    try {
      const response = await googleLogin(credentialResponse.credential);
      localStorage.setItem('token', response.token);
      alert('Signed in with Google!');
      setAuthForm({ email: '', password: '', name: '', confirmPassword: '' });
      // Reload user data
      fetchUserData();
    } catch (error) {
      console.error('Google login error:', error);
      const errorMsg = error.response?.data?.message || 'Failed to sign in with Google';
      setAuthError(errorMsg);
    }
  };

  const handleGoogleError = () => {
    setAuthError('Failed to sign in with Google. Please try again.');
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotStatus('');
    if (!forgotEmail) {
      setForgotStatus('Please enter your email address');
      return;
    }
    try {
      setForgotLoading(true);
      const response = await forgotPassword(forgotEmail);
      setForgotStatus('✅ ' + response.message);
      setForgotEmail('');
      setTimeout(() => {
        setShowForgotPassword(false);
        setForgotStatus('');
      }, 3000);
    } catch (error) {
      console.error('Forgot password error:', error);
      setForgotStatus(error.response?.data?.message || 'Failed to process request');
    } finally {
      setForgotLoading(false);
    }
  };

  if (loading) {
    return <div className="account-container"><p>Loading...</p></div>;
  }

  // Show authentication forms if not logged in
  if (!isAuthenticated) {
    const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const hasGoogleConfig = googleClientId && googleClientId !== 'YOUR_GOOGLE_CLIENT_ID';

    return (
      <GoogleOAuthProvider clientId={googleClientId || 'YOUR_GOOGLE_CLIENT_ID'}>
        <div className="account-container">
          <div className="auth-section">
            <h1>🎉 Welcome to Heritage Hub</h1>
            <p className="auth-subtitle">Sign in or create an account to save your addresses and payment methods</p>

            {!hasGoogleConfig && (
              <div className="config-warning">
                <strong>ℹ️ Google Sign-In Setup:</strong> Google OAuth is available but not yet configured. See the setup guide for details.
              </div>
            )}

            <div className="auth-tabs">
              <button
                className={`auth-tab ${authMode === 'signin' ? 'active' : ''}`}
                onClick={() => setAuthMode('signin')}
              >
                Sign In
              </button>
              <button
                className={`auth-tab ${authMode === 'signup' ? 'active' : ''}`}
                onClick={() => setAuthMode('signup')}
              >
                Create Account
              </button>
            </div>

            {showForgotPassword ? (
              <form className="auth-form" onSubmit={handleForgotPassword}>
                <h2>Reset Password</h2>
                {forgotStatus && (
                  <div className={forgotStatus.startsWith('✅') ? 'auth-success' : 'auth-error'}>
                    {forgotStatus}
                  </div>
                )}
                <p className="forgot-password-text">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
                <input
                  type="email"
                  placeholder="Email Address"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="form-input"
                  required
                />
                <button type="submit" className="btn-primary btn-full" disabled={forgotLoading}>
                  {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
                <p className="auth-link">
                  <a href="#" onClick={() => { setShowForgotPassword(false); setForgotStatus(''); setAuthError(''); }}>Back to Sign In</a>
                </p>
              </form>
            ) : (
              <form className="auth-form" onSubmit={handleSignIn}>
                <h2>Sign In</h2>
                {authError && <div className="auth-error">{authError}</div>}
                <input
                  type="email"
                  placeholder="Email Address"
                  value={authForm.email}
                  onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })}
                  className="form-input"
                  required
                />
                <input
                  type="password"
                  placeholder="Password"
                  value={authForm.password}
                  onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
                  className="form-input"
                  required
                />
                <button type="submit" className="btn-primary btn-full">
                  Sign In
                </button>
                <p className="auth-link forgot-link">
                  <a href="#" onClick={(e) => { e.preventDefault(); setShowForgotPassword(true); }}>Forgot Password?</a>
                </p>
                <hr className="form-divider" />
                {hasGoogleConfig && (
                  <div className="google-signin-button">
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={handleGoogleError}
                      text="signin_with"
                      width="100%"
                    />
                  </div>
                )}
                <p className="auth-link">
                  Don't have an account? <a href="#" onClick={() => { setAuthMode('signup'); setAuthError(''); }}>Create one</a>
                </p>
              </form>
            )}
              <form className="auth-form" onSubmit={handleSignUp}>
                <h2>Create Account</h2>
                {authError && <div className="auth-error">{authError}</div>}
                <input
                  type="text"
                  placeholder="Full Name"
                  value={authForm.name}
                  onChange={(e) => setAuthForm({ ...authForm, name: e.target.value })}
                  className="form-input"
                  required
                />
                <input
                  type="email"
                  placeholder="Email Address"
                  value={authForm.email}
                  onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })}
                  className="form-input"
                  required
                />
                <input
                  type="password"
                  placeholder="Password"
                  value={authForm.password}
                  onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
                  className="form-input"
                  required
                />
                <input
                  type="password"
                  placeholder="Confirm Password"
                  value={authForm.confirmPassword}
                  onChange={(e) => setAuthForm({ ...authForm, confirmPassword: e.target.value })}
                  className="form-input"
                  required
                />
                <button type="submit" className="btn-primary btn-full">
                  Create Account
                </button>
                <hr className="form-divider" />
                {hasGoogleConfig && (
                  <div className="google-signup-button">
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={handleGoogleError}
                      text="signup_with"
                      width="100%"
                    />
                  </div>
                )}
                <p className="auth-link">
                  Already have an account? <a href="#" onClick={() => { setAuthMode('signin'); setAuthError(''); }}>Sign in</a>
                </p>
              </form>
            )}
          </div>
        </div>
      </GoogleOAuthProvider>
    );
  }

  return (
    <div className="account-container">
      <div className="account-header">
        <h1>👤 My Account</h1>
        <button onClick={handleLogout} className="btn-logout">
          Sign Out
        </button>
      </div>

      {/* Profile Section */}
      <section className="account-section">
        <h2>Profile Information</h2>
        {editingProfile ? (
          <div className="form-group">
            <input
              type="text"
              placeholder="Full Name"
              value={profileData.name}
              onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
              className="form-input"
            />
            <input
              type="tel"
              placeholder="Phone Number"
              value={profileData.phone}
              onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
              className="form-input"
            />
            <div className="button-group">
              <button onClick={handleUpdateProfile} className="btn-primary">Save Changes</button>
              <button onClick={() => setEditingProfile(false)} className="btn-secondary">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="profile-info">
            <p><strong>Name:</strong> {user.name}</p>
            <p><strong>Email:</strong> {user.email}</p>
            <p><strong>Phone:</strong> {user.phone || 'Not provided'}</p>
            <button onClick={() => setEditingProfile(true)} className="btn-primary">Edit Profile</button>
          </div>
        )}
      </section>

      {/* Saved Addresses Section */}
      <section className="account-section">
        <h2>📍 Saved Addresses</h2>
        {addresses.length > 0 ? (
          <div className="addresses-list">
            {addresses.map(address => (
              <div key={address._id} className="address-card">
                <div className="address-header">
                  <h3>{address.label} {address.isDefault && <span className="badge-default">Default</span>}</h3>
                </div>
                <p>{address.street}</p>
                <p>{address.city}, {address.province} {address.postalCode}</p>
                <p>{address.country}</p>
                <button
                  onClick={() => handleDeleteAddress(address._id)}
                  className="btn-delete"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="no-data">No saved addresses yet</p>
        )}

        {showAddressForm ? (
          <div className="address-form">
            <h3>Add New Address</h3>
            <select
              value={newAddress.label}
              onChange={(e) => setNewAddress({ ...newAddress, label: e.target.value })}
              className="form-input"
            >
              <option value="Home">Home</option>
              <option value="Work">Work</option>
              <option value="Other">Other</option>
            </select>
            <input
              type="text"
              placeholder="Street Address"
              value={newAddress.street}
              onChange={(e) => setNewAddress({ ...newAddress, street: e.target.value })}
              className="form-input"
            />
            <input
              type="text"
              placeholder="City"
              value={newAddress.city}
              onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
              className="form-input"
            />
            <input
              type="text"
              placeholder="Province"
              value={newAddress.province}
              onChange={(e) => setNewAddress({ ...newAddress, province: e.target.value })}
              className="form-input"
            />
            <input
              type="text"
              placeholder="Postal Code"
              value={newAddress.postalCode}
              onChange={(e) => setNewAddress({ ...newAddress, postalCode: e.target.value })}
              className="form-input"
            />
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={newAddress.isDefault}
                onChange={(e) => setNewAddress({ ...newAddress, isDefault: e.target.checked })}
              />
              Set as default address
            </label>
            <div className="button-group">
              <button onClick={handleAddAddress} className="btn-primary">Save Address</button>
              <button onClick={() => setShowAddressForm(false)} className="btn-secondary">Cancel</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setShowAddressForm(true)} className="btn-primary btn-add">
            + Add New Address
          </button>
        )}
      </section>

      {/* Payment Methods Section */}
      <section className="account-section">
        <h2>💳 Payment Methods</h2>
        {paymentMethods.length > 0 ? (
          <div className="methods-list">
            {paymentMethods.map(method => (
              <div key={method._id} className="method-card">
                <div className="method-header">
                  <h3>{method.label} {method.isDefault && <span className="badge-default">Default</span>}</h3>
                </div>
                <p>Type: {method.type === 'card' ? 'Credit/Debit Card' : method.type === 'paypal' ? 'PayPal' : 'Bank Transfer'}</p>
                {method.last4 && <p>Card ending in: {method.last4}</p>}
                {method.paypalEmail && <p>PayPal: {method.paypalEmail}</p>}
              </div>
            ))}
          </div>
        ) : (
          <p className="no-data">No saved payment methods yet</p>
        )}
        <p className="info-text">Payment methods can be saved during checkout</p>
      </section>
    </div>
  );
}

export default Account;
